import { useQuery } from '@tanstack/react-query';

import { DistributorGroup } from '@/components/features/DistributorsList/DistributorsFiltersAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import { UserAutocompleteResult } from './autocomplete/useUsersAutocomplete';
import type { BaseQuery } from './baseQuery.types';

export type Distributor = components['schemas']['GetDistributorsEndpoint_DistributorResponse'];
type GetDistributorsResponse = components['schemas']['GetDistributorsEndpoint_DistributorPagedResponse'];

export interface UseDistributorsReturn extends BaseQuery {
  distributors: GetDistributorsResponse | null;
}

export function useDistributors(
  page: number,
  pageSize: number,
  sortedBy: string | null = null,
  groupNames: DistributorGroup[] | null = null,
  emails: UserAutocompleteResult[] | null = null,
): UseDistributorsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getDistributors = async (
    organizationId: number,
    page: number,
    pageSize: number,
    sortedBy: string | null,
    groupNames: string[] | null,
    emails: string[] | null,
  ) => {
    if (organizationId === 0) {
      return {
        data: [],
        page: 1,
        pageSize,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      } as GetDistributorsResponse;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.POST('/api/organizations/{OrganizationId}/distributor-groups', {
      params: {
        path: { OrganizationId: organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
      body: {
        groupNames: groupNames && groupNames.length > 0 ? groupNames : null,
        usersEmailFilter: emails && emails.length > 0 ? emails : null,
      },
    });

    if (!data) {
      return {
        items: [],
        page,
        pageSize,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      } as GetDistributorsResponse;
    }

    return data as GetDistributorsResponse;
  };

  const extractedEmails: string[] | null =
    emails && emails.length > 0 ? emails.map((u) => u.email).filter((email): email is string => typeof email === 'string') : null;

  const extractedGroupNames: string[] | null =
    groupNames && groupNames.length > 0
      ? groupNames.map((g) => g.distributorName).filter((name): name is string => typeof name === 'string')
      : null;

  const query = useQuery({
    queryKey: ['distributors', organizationId, page, pageSize, sortedBy, groupNames, emails],
    queryFn: () => getDistributors(organizationId, page, pageSize, sortedBy, extractedGroupNames, extractedEmails),
    enabled: organizationId > 0,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = query;

  return {
    distributors: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
